import { useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import * as d3 from "d3-force";
import IconButton from "@mui/material/IconButton";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import Box from "@mui/material/Box";
import CircularIndeterminate from "../../../../components/circularProgress";

const MAIN_COLOR = "#4cceac";
const BOTH_COLOR = "#68BBBC";
const SOURCE_COLOR = "#CBBA54";
const DEST_COLOR = "#c14f60";

// Builds a d3-force layout (settled once, not animated) and serializes it to an
// ECharts "graph" series — same approach as tiresia's behaviors/NetworkGraph.jsx.
// 2D and static: the simulation ticks a fixed number of times up front, then the
// chart just renders the resulting positions — no ongoing camera/physics motion.
function buildOption(ipAddress, graphData) {
  const nodes = [];
  const nodeIds = new Set();

  const addNode = (id, color) => {
    if (nodeIds.has(id)) return;
    nodeIds.add(id);
    nodes.push({ id, color });
  };

  addNode(ipAddress, MAIN_COLOR);

  // Conduit rows are per-(client,server,service) — not pre-grouped by host
  // pair like the old security-log counts were — so the same peer can show
  // up multiple times (once per service, and once per direction if traffic
  // flows both ways). Group them here into one node/edge per peer, and show
  // which services run between them instead of a connection count.
  const peers = new Map();
  (graphData || []).forEach((item) => {
    const { type: tipo, sourceIp, destIp, service } = item;
    // Determine the peer from the actual IPs, not just the `type` label —
    // a "Both" (bidirectional) conduit matches neither "Source" nor
    // "Destination", so relying on `type` alone silently drops that peer.
    const other = sourceIp === ipAddress ? destIp : destIp === ipAddress ? sourceIp : undefined;
    if (!other) return;

    let entry = peers.get(other);
    if (!entry) {
      entry = { hasSource: false, hasDest: false, services: new Set() };
      peers.set(other, entry);
    }
    if (tipo === "Both") {
      entry.hasSource = true;
      entry.hasDest = true;
    } else if (tipo === "Source") {
      entry.hasSource = true;
    } else if (tipo === "Destination") {
      entry.hasDest = true;
    } else if (sourceIp === ipAddress) {
      entry.hasSource = true;
    } else {
      entry.hasDest = true;
    }
    if (service) entry.services.add(service);
  });

  const links = [];
  peers.forEach((entry, other) => {
    const both = entry.hasSource && entry.hasDest;
    addNode(other, both ? BOTH_COLOR : entry.hasSource ? SOURCE_COLOR : DEST_COLOR);

    const label = [...entry.services].join(", ") || "—";
    if (entry.hasDest && !entry.hasSource) {
      links.push({ source: other, target: ipAddress, label });
    } else {
      links.push({ source: ipAddress, target: other, label });
    }
  });

  if (nodes.length <= 1) return { nodes: [], links: [], option: null };

  const d3nodes = nodes.map((n, i) => {
    if (n.id === ipAddress) return { ...n, x: 0, y: 0, fx: 0, fy: 0 };
    const angle = (i / nodes.length) * 2 * Math.PI;
    return { ...n, x: 120 * Math.cos(angle), y: 120 * Math.sin(angle) };
  });

  d3.forceSimulation(d3nodes)
    .force("link", d3.forceLink(links).id((d) => d.id).distance(90).strength(0.6))
    .force("charge", d3.forceManyBody().strength(-160))
    .force("collide", d3.forceCollide().radius(24).strength(0.9))
    .force("center", d3.forceCenter(0, 0).strength(0.05))
    .velocityDecay(0.45)
    .stop()
    .tick(150);

  const ecNodes = d3nodes.map((n) => ({
    id: n.id,
    name: n.id,
    x: Number.isFinite(n.x) ? n.x : 0,
    y: Number.isFinite(n.y) ? n.y : 0,
    symbolSize: n.id === ipAddress ? 26 : 16,
    itemStyle: { color: n.color, borderColor: "#111", borderWidth: 1 },
    label: { show: true, formatter: n.id, fontSize: 10, color: "#c8c8c8", position: "top", distance: 4 },
  }));

  // d3-force's forceLink mutates each link's source/target from a plain id string
  // into the actual node object reference once the simulation ticks — read the id
  // back off it, otherwise ECharts can't resolve the edge against any node and
  // silently renders no line at all.
  const ecLinks = links.map((l) => ({
    source: l.source?.id ?? l.source,
    target: l.target?.id ?? l.target,
    label: l.label,
    lineStyle: { color: "#8a8a8a", width: 1.4, opacity: 0.75, curveness: 0 },
  }));

  return {
    nodes: ecNodes,
    links: ecLinks,
    option: {
      backgroundColor: "transparent",
      tooltip: {
        trigger: "item",
        confine: true,
        backgroundColor: "#2c2c2a",
        borderColor: "#555",
        textStyle: { color: "#c2c0b6", fontSize: 12 },
        formatter: (p) => {
          if (p.dataType === "edge") return p.data.label ?? "";
          return `IP: ${p.data.name}`;
        },
      },
      series: [{
        type: "graph",
        layout: "none",
        data: ecNodes,
        links: ecLinks,
        roam: true,
        draggable: false,
        edgeSymbol: ["none", "arrow"],
        edgeSymbolSize: 6,
        emphasis: { focus: "adjacency", scale: false, lineStyle: { width: 2, opacity: 0.9 } },
        blur: { itemStyle: { opacity: 0.15 }, lineStyle: { opacity: 0.08 }, label: { show: false } },
      }],
    },
  };
}

// "Connections" overview graph — 2D, static (settles once, never auto-moves).
// Purely visual: a node/edge is already fully described by the table below
// it, no click-to-detail popup.
const MainFG = ({ ipAddress, graphData = [], charged }) => {
  const echartsRef = useRef(null);
  const built = useMemo(() => buildOption(ipAddress, graphData), [ipAddress, graphData]);

  const handleFit = () => {
    echartsRef.current?.getEchartsInstance()?.dispatchAction({ type: "restore" });
  };

  if (!built.option) {
    return <CircularIndeterminate charged={charged} title="No connections in this time range" />;
  }

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
      <ReactECharts
        ref={echartsRef}
        option={built.option}
        notMerge={true}
        style={{ width: "100%", height: "100%" }}
        opts={{ renderer: "canvas" }}
      />
      <IconButton
        size="small"
        title="Fit all nodes"
        onClick={handleFit}
        sx={{
          position: "absolute", bottom: 8, right: 8,
          bgcolor: "background.paper", border: "1px solid", borderColor: "divider",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <FitScreenIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default MainFG;
